import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { FileRecord, ImportFileRequest } from "@/shared/domain/files";

import { fileQueryKeys } from "./fileQueryKeys";

export interface ImportFilesParams {
  requests: ImportFileRequest[];
  orgId: string;
}

/**
 * Import files that already exist in an S3 bucket into a dataset, by reference.
 *
 * Invalidates the dataset views a new file changes: its directory listing, the item count, and
 * the extension filter built from them. Org-wide file searches key on an org this mutation is
 * not told, so they refresh on their own staleness.
 */
export function useImportFilesMutation() {
  const { files: fileService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<FileRecord[], Error, ImportFilesParams>({
    mutationFn: ({ requests, orgId }) =>
      fileService.importFiles(requests, orgId),
    onSuccess: () => {
      return Promise.all(
        [
          fileQueryKeys.directoryItemCount(),
          fileQueryKeys.directoryContents(),
          fileQueryKeys.directoryExtensions(),
        ].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey, exact: false }),
        ),
      );
    },
  });
}
